import { ChatModel } from '@agent/agent-interface/contact-view/chat/model/chat-model';
import { Injectable } from '@angular/core';
import * as OT from '@opentok/client';
import { BehaviorSubject, Observable } from 'rxjs';

import { isNotNullOrUndefined, Logger } from '@contact/upgrade';

import Subscriber = OT.Subscriber;

export type StreamAndTargetEl = {
  stream: OT.Stream;
  chat: ChatModel;
  $el: HTMLElement;
};

@Injectable({
  providedIn: 'root',
})
export class OpenTokService {
  private session: OT.Session | null;
  private chat: ChatModel;
  private token: string;

  private readonly _streamSubscribed$ = new BehaviorSubject<StreamAndTargetEl | null>(null);
  readonly streamSubscribed$ = this._streamSubscribed$.asObservable().pipe(isNotNullOrUndefined());

  private subscriber: Subscriber | null;
  private htmlElement: HTMLElement | null;
  private fullSizeDimension = {
    width: '',
    height: '',
  };
  private isFullSize = true;

  constructor(private readonly logger: Logger) {}

  initSession(apiKey: string, token: string, sessionId: string, $el: HTMLElement, chat: ChatModel): void {
    if (this.session) {
      throw new Error('Opentok session already started. Please use only one at a time. Use "stopPreviousSession" to close the older one.');
    }
    this.session = OT.initSession(apiKey, sessionId);
    this.chat = chat;
    this.token = token;

    this.session.on('streamCreated', event => {
      const subOptions: OT.SubscriberProperties = {
        width: '100%',
        height: '100%',
        showControls: false,
        insertMode: 'append',
      };
      this.htmlElement = $el;
      this.subscriber = this.session!.subscribe(event.stream, $el, subOptions, err => {
        if (err) {
          this.logger.error(`Error when subscribing to the stream ${event}`);
          this._streamSubscribed$.error(err);
        } else {
          this.fullSizeDimension.width = event.stream.videoDimensions.width + 'px';
          this.fullSizeDimension.height = event.stream.videoDimensions.height + 'px';
          this._streamSubscribed$.next({ stream: event.stream, $el, chat: this.chat });
        }
      });
    });
  }

  changeResolution(): void {
    if (this.subscriber) {
      this.isFullSize = !this.isFullSize;
      if (this.htmlElement) {
        if (this.isFullSize) {
          this.htmlElement.style.width = this.fullSizeDimension.width;
          this.htmlElement.style.height = this.fullSizeDimension.height;
        } else {
          this.htmlElement.style.width = '100%';
          this.htmlElement.style.height = '100%';
        }
      }
    }
  }

  connect(): Observable<OT.Session> {
    return new Observable(subscriber => {
      if (this.session) {
        this.session.connect(this.token, err => {
          if (err) {
            subscriber.error(err);
          } else {
            subscriber.next(this.session!);
            subscriber.complete();
          }
        });
      }
    });
  }

  stopPreviousSession() {
    if (this.session) {
      this.session.off();
      this.session.disconnect();
      this.session = null;
      this._streamSubscribed$.next(null);
    }
    if (this.subscriber) {
      this.subscriber.off();
      this.subscriber = null;
    }

    if (this.htmlElement) {
      this.htmlElement = null;
    }
  }
}
